import { memo, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useMediaQuery from "@mui/material/useMediaQuery";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AutoplayVideoRoot from "./components/AutoplayVideoRoot";
import AutoplayVideoElement from "./components/AutoplayVideoElement";
import AutoplayVideoToggleButton from "./components/AutoplayVideoToggleButton";

export interface AutoplayVideoProps {
  src: string;
  /** Text alternative for the animation, like an image's alt. */
  alt: string;
  /** Frame shown until playback starts, and while paused under reduced motion. */
  poster?: string;
  className?: string;
}

/**
 * Muted, looping video for short animated demos, used instead of a GIF.
 * Always shows a pause control (WCAG 2.2.2) and does not autoplay when the user prefers reduced motion.
 */
function AutoplayVideo(props: AutoplayVideoProps) {
  const { src, alt, poster, className } = props;

  const { t } = useTranslation();
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)", { noSsr: true });
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  const handlePlay = useCallback(() => setPlaying(true), []);
  const handlePause = useCallback(() => setPlaying(false), []);

  const togglePlaying = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (playing) {
      video.pause();
    } else {
      video.play().catch(handlePause);
    }
  }, [handlePause, playing]);

  return (
    <AutoplayVideoRoot className={className}>
      <AutoplayVideoElement
        ref={videoRef}
        src={src}
        poster={poster}
        role="img"
        aria-label={alt}
        autoPlay={!prefersReducedMotion}
        muted
        loop
        playsInline
        onPlay={handlePlay}
        onPause={handlePause}
      />
      <AutoplayVideoToggleButton
        size="small"
        aria-label={playing ? t("global.pause_animation_label") : t("global.play_animation_label")}
        onClick={togglePlaying}
      >
        {playing ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
      </AutoplayVideoToggleButton>
    </AutoplayVideoRoot>
  );
}

export default memo(AutoplayVideo);
